import { memo as Memo, useMemo, useEffect, useCallback, useRef } from 'react';
import * as THREE from 'three';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import gsap from 'gsap/all';

//* HOC's
import withUIContext from '@context/consumerHOC/UIConsumer';

//* Components
import Text from '@components/global/Text';

//* Styles
import GameStyle from './style';

//* @refresh reset
let scene,
	camera,
	renderer,
	halfHeight,
	halfWidth,
	ballSize,
	paddleWidth,
	paddleX = 0,
	deltaX,
	deltaY,
	animFrame;

const Game = Memo((props) => {
	//! Refs
	const contRef = useRef();
	const ref = useRef();

	const params = useMemo(
		() => ({
			fov: 45,
			near: 0.1,
			far: 10000,
			cameraZ: 1000,
			initialSpeed: 2,
			speed: 2,
			ballRot: {
				x: 0.001,
				y: 0.002,
				z: 0.0015,
			},
		}),
		[]
	);

	useEffect(() => {
		updateBestScore(localStorage.getItem('highScore') || 0);

		deltaX = -params.speed;
		deltaY = -params.speed;

		const el = contRef?.current?.getBoundingClientRect();

		scene = new THREE.Scene();
		camera = new THREE.OrthographicCamera(el.width / -2, el.width / 2, el.height / 2, el.height / -2, params.near, params.far);
		renderer = new THREE.WebGLRenderer({ canvas: ref.current, alpha: true });

		//! Camera Calculation
		calculateCameraAndRenderer();

		//! Axes Helper
		// const axesHelper = new THREE.AxesHelper(params.cameraZ);
		// scene.add(axesHelper);

		//! Creating Paddle
		createPaddle();

		//! Creating Ball
		createBall().then(() => {
			//! Animate
			animate();
		});

		//! Adding Event Listener
		window.addEventListener('mousemove', onMouseMove);
		window.addEventListener('resize', onWindowResize);
		window.addEventListener('deviceorientation', handleOrientationEvent, true);

		const scoreCont = document.querySelector('.scoreCont');
		scoreCont.click();

		return () => {
			window.removeEventListener('mousemove', onMouseMove);
			window.removeEventListener('resize', onWindowResize);
			window.removeEventListener('deviceorientation', handleOrientationEvent);

			animFrame && cancelAnimationFrame(animFrame);

			while (scene?.children.length > 0) {
				scene.remove(scene.children[0]);
			}
		};
	}, [props.darkMode]);

	//! Camera aspect
	const calculateCameraAndRenderer = useCallback(() => {
		const el = contRef?.current?.getBoundingClientRect();

		if (el) {
			const aspect = el.width / el.height;
			halfWidth = el.width / 2;
			halfHeight = el.height / 2;

			renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
			renderer.setSize(el.width, el.height);

			camera.left = -el.width / 2;
			camera.right = el.width / 2;
			camera.top = el.height / 2;
			camera.bottom = -el.height / 2;
			camera.aspect = aspect;
			camera.position.z = params.cameraZ;
			camera.updateProjectionMatrix();
		}
	}, [props.darkMode]);

	//! Creating Paddle
	const createPaddle = useCallback(() => {
		paddleWidth = halfWidth / 2;
		const paddleGeometry = new THREE.PlaneGeometry(1, 1, 1);
		const paddleMaterial = new THREE.MeshBasicMaterial({ color: props.darkMode ? 0xffffff : 0x000000 });
		const paddle = new THREE.Mesh(paddleGeometry, paddleMaterial);
		paddle.position.set(-paddleWidth / 2, -halfHeight);
		paddle.scale.set(paddleWidth, 15, 1);
		paddle.name = 'paddle';
		scene.add(paddle);
	}, [props.darkMode]);

	//! Updating Paddle
	const updatePaddleSize = useCallback(() => {
		const paddle = scene.getObjectByName('paddle');
		paddleWidth = halfWidth / 2;
		paddle.scale.set(paddleWidth, 15, 1);
	}, [props.darkMode]);

	//! Creating Ball
	const createBall = useCallback(() => {
		ballSize = halfWidth > halfHeight ? halfWidth / 15 : halfHeight / 15;

		const loader = new OBJLoader();
		return new Promise((resolve, reject) => {
			loader.load(
				'/rolly.obj',
				(object) => {
					object.name = 'ball';

					const sphere = object.getObjectByName('sphere');
					sphere.material = new THREE.MeshBasicMaterial({ color: props.darkMode ? 0xffffff : 0x000000 });
					sphere.geometry.computeBoundingSphere();

					const scaleSize = ballSize / sphere.geometry.boundingSphere.radius;
					object.scale.set(scaleSize, scaleSize, scaleSize);

					const bumpGroup = new THREE.Group();
					bumpGroup.name = 'bumpGroup';
					const eyesGroup = new THREE.Group();
					eyesGroup.name = 'eyesGroup';

					const children = object.children;
					for (let i = object.children.length - 2; i >= 0; i--) {
						children[i].material = new THREE.MeshBasicMaterial({ color: props.darkMode ? 0xffffff : 0x000000 });
						children[i].material.visible = false;

						if (children[i].name.includes('eye')) {
							children[i].name = children[i].name === 'eye1' ? 'active' : children[i].name;
							eyesGroup.add(children[i]);
						} else if (children[i].name.includes('back')) {
							children[i].name = children[i].name === 'back1' ? 'active' : children[i].name;
							bumpGroup.add(children[i]);
						}
					}

					object.add(bumpGroup);
					object.add(eyesGroup);
					scene.add(object);
					updateGroupItem('eyesGroup');
					updateGroupItem('bumpGroup');

					resolve();
				},
				(xhr) => {
					console.log((xhr.loaded / xhr.total) * 100 + '% loaded');
				},
				(error) => {
					console.log('An error happened  ' + error);
				}
			);
		});
	}, [props.darkMode]);

	//! Updating Ball Size
	const updateBallSize = useCallback(() => {
		ballSize = halfWidth > halfHeight ? halfWidth / 15 : halfHeight / 10;
		const ball = scene.getObjectByName('ball');
		const sphere = scene.getObjectByName('sphere', true);
		sphere.geometry.computeBoundingSphere();

		const scaleSize = ballSize / sphere.geometry.boundingSphere.radius;
		ball.scale.set(scaleSize, scaleSize, scaleSize);
	}, [props.darkMode]);

	//! Update Ball Position
	const updatePositions = useCallback(() => {
		const paddle = scene.getObjectByName('paddle', true);
		const ball = scene.getObjectByName('ball', true);
		let change = false;

		paddle.position.set(paddleX, -halfHeight);

		const { x, y, z } = ball.position;

		ball.rotation.x = THREE.MathUtils.degToRad(x + params.ballRot.x);
		ball.rotation.y = THREE.MathUtils.degToRad(y + params.ballRot.y);
		ball.rotation.z = THREE.MathUtils.degToRad(z + params.ballRot.z);

		if (x <= -halfWidth + ballSize && deltaX < 0) {
			deltaX = params.speed;

			change = true;
		} else if (x >= halfWidth - ballSize && deltaX > 0) {
			deltaX = -params.speed;
			change = true;
		}

		if (y <= -halfHeight + ballSize && deltaY < 0) {
			const el = document.querySelector('.score');
			const score = el && parseInt(el.firstChild.nodeValue);

			if (x >= paddle.position.x - paddleWidth / 2 && x <= paddle.position.x + paddleWidth / 2 && y > -halfHeight + ballSize / 2) {
				const newScore = typeof score === 'number' ? 1 + score : newScore;
				el.firstChild.nodeValue = newScore;

				if (localStorage.getItem('highScore') < newScore) {
					updateBestScore(newScore);
				}

				deltaY = params.speed += 0.5;
				change = true;
			} else {
				if (-halfHeight - ballSize >= y) {
					params.speed = params.initialSpeed;
					deltaX = deltaY = params.initialSpeed;
					if (el) {
						el.firstChild.nodeValue = 0;
					}
					newGame();
					return '';
				}
			}
		} else if (y >= halfHeight - ballSize && deltaY > 0) {
			deltaY = -params.speed;
			change = true;
		}

		if (change) {
			updateGroupItem('bumpGroup');
			updateGroupItem('eyesGroup');
		}

		ball.position.set(x + deltaX, y + deltaY);
	}, [props.darkMode]);

	const updateBestScore = useCallback(
		(newScore) => {
			localStorage.setItem('highScore', newScore);
			const bestScoreEl = document.querySelector('.best-score');
			if (bestScoreEl) {
				bestScoreEl.firstChild.nodeValue = newScore;
			}
		},
		[props.darkMode]
	);

	const updateGroupItem = useCallback(
		(groupName) => {
			const group = scene.getObjectByName(groupName, true);

			const active = group.children.findIndex((x) => x.name === 'active');

			let newNum = active;

			do {
				newNum = Math.round(Math.random() * (group.children.length - 1));
			} while (newNum === active);
			group.children[newNum].material.visible = true;
			group.children[newNum].material.color = new THREE.Color(props.darkMode ? '#000000' : '#ffffff');
			group.children[newNum].name = 'active';
			group.children[active].material.visible = false;
			group.children[active].name = active;
		},
		[props.darkMode]
	);

	const newGame = useCallback(() => {
		params.speed = params.initialSpeed;
		const ball = scene.getObjectByName('ball');
		const rand = Math.random() < 0.5 ? -1 : 1;
		ball.position.set(Math.random() * (rand * halfWidth + ballSize), halfHeight + ballSize);
	}, [props.darkMode]);

	const handleOrientationEvent = useCallback(
		(e) => {
			const minMaxDeg = 30;
			const gamma = e.gamma > minMaxDeg ? minMaxDeg : e.gamma < -minMaxDeg ? -minMaxDeg : e.gamma;
			const percent = gamma / minMaxDeg;
			paddleX = (halfWidth - paddleWidth / 2) * percent;
		},
		[props.darkMode]
	);

	//! Mouse Move
	const onMouseMove = useCallback(
		(e) => {
			const winHalf = window.innerWidth / 2;
			const percent = (e.clientX - winHalf) / winHalf;

			paddleX = (halfWidth - paddleWidth / 2) * percent;
		},
		[props.darkMode]
	);

	//! Window Resize
	const onWindowResize = useCallback(() => {
		updateView();
	}, [props.darkMode]);

	//! Update View
	const updateView = useCallback(() => {
		calculateCameraAndRenderer();
	}, [props.darkMode]);

	//! Animation
	const animate = () => {
		animFrame = requestAnimationFrame(animate);

		//! for auto game
		// const ball = scene?.getObjectByName('ball', true);
		// if (ball) {
		// 	paddleX = ball.position.x;
		// }

		updatePositions();
		updatePaddleSize();
		updateBallSize();

		renderer.render(scene, camera);
	};

	useEffect(() => {
		if (!contRef.current) return;
		gsap.to(contRef.current, {
			opacity: 1,
			duration: 0.3,
			delay: 0.3,
		});
		return () => {
			gsap.set(contRef.current, {
				opacity: 0,
			});
		};
	}, [props.active]);

	return (
		<GameStyle
			ref={contRef}
			className={`game`}>
			<div className={`scoreCont container pointCont`}>
				<div>
					<Text text='yourBestScore' />
					<Text
						tag={'span'}
						className='best-score font-medium h6 font-poppins'>
						0
					</Text>
				</div>

				<div>
					<Text text='yourCurrentScore' />
					<Text
						tag={'span'}
						className='score font-medium h6 font-poppins'>
						0
					</Text>
				</div>
			</div>

			<canvas ref={ref} />
		</GameStyle>
	);
});

export default withUIContext(Game, ['darkMode']);
